import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { usePageTitle } from '../../context/PageTitleContext';
import { useSidebar } from '../../context/SidebarContext';
import { advancedReportService } from '../../services/api';
import { Card, Table, PageHeader, Pagination } from '../common';
import { default as usePageState } from '../../hooks/usePageState';
import { downloadExcel } from '../../utils/exportUtils';
import { useAuth } from '../../context/AuthContext';
import useDepartment from '../../hooks/useDepartment';

const RegionReport = () => {
  const { setPageTitle, setPageSubtitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const { user, getPagePermissions } = useAuth();
  const dept = useDepartment();
  // Category Type is IT's Software/Hardware split; BioMedical has no such
  // division, so the column is omitted there.
  const showCategoryType = (user?.department_code || 'IT') === 'IT';

  const permissions = getPagePermissions('/reports/region');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ regions: [], categoryTypes: [] });

  const [filters, setFilters] = usePageState('regionReport', {
    start_date: '',
    end_date: '',
    region_code: '',
    category_type: '',
    currentPage: 1,
    itemsPerPage: 30,
  }, ['/reports']);

  useEffect(() => {
    setPageTitle(`${dept.region} Report`);
    setPageSubtitle(dept.isBioMedical ? 'Entity-wise performance analysis' : 'Regional performance analysis');
    fetchFilterOptions();
  }, [setPageTitle, setPageSubtitle, dept.region, dept.isBioMedical]);

  const fetchFilterOptions = async () => {
    try {
      const response = await advancedReportService.getFilters();
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.filter = 'custom';
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.region_code) params.region_code = filters.region_code;
      if (filters.category_type) params.category_type = filters.category_type;
      const response = await advancedReportService.getRegionReport(params);
      setData(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching region report:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.start_date, filters.end_date, filters.region_code, filters.category_type]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatHours = (hours) => {
    if (!hours || hours === 0) return '-';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours}h`;
  };

  const getStatusBadge = (label) => {
    const bgMap = {
      'Excellent': 'bg-green-100 text-green-800',
      'Average': 'bg-yellow-100 text-yellow-800',
      'Poor': 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${bgMap[label] || 'bg-gray-100 text-gray-800'}`}>
        {label}
      </span>
    );
  };

  // Pagination
  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / filters.itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (filters.currentPage - 1) * filters.itemsPerPage;
    return data.slice(start, start + filters.itemsPerPage);
  }, [data, filters.currentPage, filters.itemsPerPage]);

  const handleDownload = () => {
    downloadExcel(data, columns, `${dept.region.toLowerCase()}_report`);
  };

  const columns = [
    {
      header: 'S.No',
      accessor: 'serial',
      width: '60px',
      sticky: true,
      render: (_, __, index) => (filters.currentPage - 1) * filters.itemsPerPage + index + 1
    },
    {
      header: dept.region,
      accessor: 'region',
      sticky: true,
      render: (value) => <span className="font-semibold text-gray-900">{value}</span>
    },
    ...(showCategoryType ? [{
      header: 'Category Type',
      accessor: 'categoryType',
      render: (value) => <span className="text-gray-700 font-medium">{value || '-'}</span>
    }] : []),
    {
      header: 'No of Employees',
      accessor: 'employees',
      align: 'center',
      render: (value) => <span className="font-medium text-gray-700">{value}</span>
    },
    {
      header: 'Total Tickets',
      accessor: 'totalTickets',
      align: 'center',
      render: (value) => <span className="font-semibold">{value}</span>
    },
    {
      header: 'Tickets/Employee',
      accessor: 'ticketsPerEmployee',
      align: 'center',
      render: (value) => <span className="text-gray-700 font-medium">{value}</span>
    },
    {
      header: 'Resolved',
      accessor: 'resolved',
      align: 'center',
      render: (value) => <span className="font-semibold text-green-600">{value}</span>
    },
    {
      header: 'Active',
      accessor: 'active',
      align: 'center',
      render: (value) => (
        <span className={`font-semibold ${value > 0 ? 'text-blue-600' : 'text-gray-500'}`}>{value}</span>
      )
    },
    {
      header: 'Avg Resolution Time',
      accessor: 'avgResolutionTime',
      align: 'center',
      render: (value) => <span className="text-gray-700">{formatHours(value)}</span>
    },
    {
      header: 'Resolution %',
      accessor: 'resolutionPct',
      align: 'center',
      render: (value) => (
        <div className="flex items-center justify-center gap-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[60px]">
            <div
              className="h-2 rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(value, 100)}%`,
                backgroundColor: value >= 80 ? '#10b981' : value >= 50 ? '#f59e0b' : '#ef4444'
              }}
            />
          </div>
          <span className="font-medium text-sm" style={{ color: value >= 80 ? '#059669' : value >= 50 ? '#d97706' : '#dc2626' }}>
            {value}%
          </span>
        </div>
      )
    },
    {
      header: 'Escalation Rate',
      accessor: 'escalationRate',
      align: 'center',
      render: (value) => (
        <span className={`font-medium ${value > 10 ? 'text-red-600' : value > 5 ? 'text-yellow-600' : 'text-green-600'}`}>
          {value}%
        </span>
      )
    },
    {
      header: 'Reopen Rate',
      accessor: 'reopenRate',
      align: 'center',
      render: (value) => (
        <span className={`font-medium ${value > 10 ? 'text-red-600' : value > 5 ? 'text-yellow-600' : 'text-green-600'}`}>
          {value}%
        </span>
      )
    },
    {
      header: 'Top Category',
      accessor: 'topCategory',
      render: (value) => <span className="text-gray-700 text-xs">{value}</span>
    },
    {
      header: 'Overall Score',
      accessor: 'overallScore',
      align: 'center',
      render: (value) => (
        <span className="text-lg font-bold" style={{ color: value >= 75 ? '#059669' : value >= 50 ? '#d97706' : '#dc2626' }}>
          {value}%
        </span>
      )
    },
    {
      header: 'Status',
      accessor: 'statusLabel',
      align: 'center',
      sticky: 'right',
      render: (value) => getStatusBadge(value)
    },
  ];

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader
          filters={{
            dateRange: {
              startDate: filters.start_date,
              endDate: filters.end_date,
              onStartDateChange: (e) => setFilters({ ...filters, start_date: e.target.value, currentPage: 1 }),
              onEndDateChange: (e) => setFilters({ ...filters, end_date: e.target.value, currentPage: 1 }),
            },
            dropdowns: [
              {
                value: filters.region_code,
                onChange: (e) => setFilters({ ...filters, region_code: e.target.value, currentPage: 1 }),
                options: [{ value: '', label: `All ${dept.regions}` }, ...(filterOptions.regions || [])],
                placeholder: `All ${dept.regions}`,
                searchable: true,
                width: '180px'
              },
              // Category Type is IT-only, like the column it filters
              ...(showCategoryType ? [{
                value: filters.category_type,
                onChange: (e) => setFilters({ ...filters, category_type: e.target.value, currentPage: 1 }),
                options: [{ value: '', label: 'All Category Types' }, ...(filterOptions.categoryTypes || [])],
                placeholder: 'All Category Types',
                searchable: true,
                width: '180px'
              }] : [])
            ]
          }}
          downloadButton={permissions.can_download ? {
            label: 'Download',
            onClick: handleDownload,
            variant: 'outline',
            className: 'border-primary-600 text-primary-600 hover:bg-primary-50 focus:ring-primary-500',
            disabled: loading || data.length === 0
          } : null}
        />
        <div className="mb-2 px-1">
          <div className="flex flex-wrap gap-2 mb-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">Excellent: Score &ge; 75%</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Average: Score &ge; 50%</span>
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Poor: Score &lt; 50%</span>
          </div>
          <div className="bg-blue-50 border-l-4 border-blue-400 p-2 rounded">
            <p className="text-xs text-blue-800">
              <strong>Overall Score</strong> = Resolution % (40%) + (100 - Escalation Rate) (30%) + (100 - Reopen Rate) (30%).
              <strong> Tickets/Employee</strong> = Total Tickets / No of Employees in the region.
            </p>
          </div>
        </div>
        <Table
          columns={columns}
          data={paginatedData}
          loading={loading}
          emptyMessage={dept.isBioMedical
            ? 'No entity data available for the selected filters'
            : 'No regional data available for the selected filters'}
        />
        <Pagination
          currentPage={filters.currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={filters.itemsPerPage}
          onPageChange={(page) => setFilters({ ...filters, currentPage: page })}
          onItemsPerPageChange={(count) => setFilters({ ...filters, itemsPerPage: count, currentPage: 1 })}
        />
      </Card>
    </div>
  );
};

export default RegionReport;
